/**
 * utils/jsonHandler.js - Gestionnaire JSON générique
 * Validation et traitement des fichiers JSON
 */

/**
 * Valider un fichier avant traitement
 * @param {File} file - Fichier à valider
 * @param {Object} options - Options de validation
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
function _validateFile(file, options = {}) {
  const errors = [];
  const defaultOptions = {
    maxSize: 10 * 1024 * 1024, // 10MB
    allowedExtensions: [".json"],
    ...options,
  };

  if (!file) {
    errors.push("Aucun fichier fourni");
    return { isValid: false, errors };
  }

  // Vérifier l'extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = defaultOptions.allowedExtensions.some((ext) => fileName.endsWith(ext));

  if (!hasValidExtension) {
    errors.push(`Extension invalide. Formats acceptés: ${defaultOptions.allowedExtensions.join(", ")}`);
  }

  // Vérifier la taille
  if (file.size > defaultOptions.maxSize) {
    const maxSizeMB = Math.round(defaultOptions.maxSize / 1024 / 1024);
    errors.push(`Fichier trop volumineux (max: ${maxSizeMB}MB)`);
  }

  // Vérifier que le fichier n'est pas vide
  if (file.size === 0) {
    errors.push("Le fichier est vide");
  }

  return {
    isValid: errors.length === 0,
    errors: errors,
  };
}

/**
 * Valider et parser le contenu JSON
 * @param {string} jsonContent - Contenu JSON en string
 * @returns {Object} { isValid: boolean, errors: string[], data: Object|null }
 */
function _validateJsonContent(jsonContent) {
  const errors = [];
  let parsedData = null;

  if (!jsonContent || !jsonContent.trim()) {
    errors.push("Le contenu JSON est vide");
    return { isValid: false, errors, data: null };
  }

  try {
    parsedData = JSON.parse(jsonContent);
  } catch (parseError) {
    errors.push(`Format JSON invalide: ${parseError.message}`);
    return { isValid: false, errors, data: null };
  }

  return {
    isValid: true,
    errors: [],
    data: parsedData,
  };
}

/**
 * Lire et valider un fichier JSON complet
 * @param {File} file - Fichier à traiter
 * @param {Object} options - Options de validation
 * @returns {Promise<Object>} { isValid: boolean, errors: string[], data: Object|null, file: File }
 */
export async function readJsonFile(file, options = {}) {
  // Validation du fichier
  const fileValidation = _validateFile(file, options);
  if (!fileValidation.isValid) {
    return {
      isValid: false,
      errors: fileValidation.errors,
      data: null,
      file: file,
    };
  }

  try {
    // Lecture du contenu
    const text = await file.text();

    // Validation du JSON
    const contentValidation = _validateJsonContent(text);
    if (!contentValidation.isValid) {
      return {
        isValid: false,
        errors: contentValidation.errors,
        data: null,
        file: file,
      };
    }

    return {
      isValid: true,
      errors: [],
      data: contentValidation.data,
      file: file,
    };
  } catch (error) {
    return {
      isValid: false,
      errors: [`Erreur de lecture: ${error.message}`],
      data: null,
      file: file,
    };
  }
}

/**
 * Formater la taille de fichier en unités lisibles
 * @param {number} bytes - Taille en bytes
 * @returns {string} Taille formatée
 */
function _formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  if (typeof bytes !== "number" || bytes < 0) return "Taille inconnue";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

/**
 * Extraire les informations du fichier
 * @param {File} file - Objet File
 * @returns {Object} Informations formatées du fichier
 */
export function extractFileInfo(file) {
  return {
    name: file.name,
    size: file.size,
    sizeFormatted: _formatFileSize(file.size),
    type: file.type,
    lastModified: file.lastModified,
    lastModifiedDate: file.lastModified ? new Date(file.lastModified) : null,
    lastModifiedFormatted: file.lastModified
      ? new Intl.DateTimeFormat("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(new Date(file.lastModified))
      : "Inconnue",
  };
}
